import { TSplitTestNames } from '@api/lendingAPI/lapiHooks';
import { datadogLogs } from '@datadog/browser-logs';
import { TAccount } from '@helpers/Accounts/accounts.types';
import { getAccountFundedFormalOffer } from '@helpers/Accounts/getAccountFundedFormalOffer';
import { getAccountIsFlexiLoan } from '@helpers/Accounts/getAccountIsFlexiLoan';
import { getAccountIsInterestOnly } from '@helpers/Accounts/getAccountIsInterestOnly';
import { getAccountIsRBL } from '@helpers/Accounts/getAccountIsRBL';
import { getAccountIsRCF } from '@helpers/Accounts/getAccountIsRCF';
import { getAccountIsTermLoan } from '@helpers/Accounts/getAccountIsTermLoan';
import { getIsMicroLoanFundedAccount } from '@helpers/Accounts/getIsMicroLoanFundedAccount';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { getTopUpStatus } from '@helpers/GetTopUpStatus/getTopUpStatus';
import { TApplication } from '@helpers/GetTopUpStatus/getTopUpStatus.types';

const ACCOUNT_FEATURES = {
    flexi_loan:
        '12 month or more duration with no interest-only repayment period',
    micro_loan: '3 month loan duration',
    micro_loan_plus: '6 month loan duration',
    interest_only: 'interest-only repayment period, usually 3 months',
    interest_only_plus:
        'interest-only repayment period with an access period, usually 6 months',
    revenue_based_loan: 'merchant_cash_advance product type',
    topup_eligible: 'topup eligible account',
    topup_approved: 'topup approved account',
    topup_pending: 'topup pending account',
    interest_only_extension: 'account approved for an interest-only extension',
    term_loan: 'term loan or business loan product type',
    revolving_credit_facility:
        'interest only repayment period with an access period of 12 months'
} as const;

export type TAccountFeatureKey = keyof typeof ACCOUNT_FEATURES;
export type TAccountFeatureMap = Record<TAccountFeatureKey, boolean>;

export type TSplitTestBranch = 'INCLUDE' | 'EXCLUDE' | string;
export type TSplitTestAssignments = {
    [K in TSplitTestNames]: TSplitTestBranch;
};

export class AccountFeatureEvaluator {
    private readonly flags: TAccountFeatureMap;
    constructor(
        account: NonNullable<TAccount>,
        application: NonNullable<TApplication>
    ) {
        this.flags = {} as TAccountFeatureMap;
        for (const key of Object.keys(
            ACCOUNT_FEATURES
        ) as TAccountFeatureKey[]) {
            try {
                this.flags[key] = this.evaluateFeature(
                    key,
                    account,
                    application
                );
            } catch (err) {
                datadogLogs.logger.error(`Error evaluating feature ${key}`, {
                    error: err
                });
                this.flags[key] = false;
            }
        }
    }
    private evaluateFeature(
        feature: TAccountFeatureKey,
        account: TAccount,
        application: TApplication
    ): boolean {
        try {
            switch (feature) {
                case 'flexi_loan':
                    return getAccountIsFlexiLoan(account);
                case 'term_loan':
                    return getAccountIsTermLoan(account);
                case 'micro_loan':
                    return (
                        getIsMicroLoanFundedAccount(account) &&
                        getDurationInMonths(account.duration!) === 3
                    );
                case 'micro_loan_plus':
                    return (
                        getIsMicroLoanFundedAccount(account) &&
                        getDurationInMonths(account.duration!) === 6
                    );
                case 'interest_only':
                    return (
                        getAccountIsInterestOnly(account) &&
                        getAccountFundedFormalOffer(account)
                            ?.interest_only_repayments === 3
                    );
                case 'interest_only_plus':
                    return (
                        getAccountIsInterestOnly(account) &&
                        getAccountFundedFormalOffer(account)
                            ?.interest_only_repayments === 6
                    );
                case 'revenue_based_loan':
                    return getAccountIsRBL(account);
                case 'topup_eligible':
                    return (
                        getTopUpStatus(account, application) ===
                        'TOPUP_ELIGIBLE'
                    );
                case 'topup_approved':
                    return (
                        getTopUpStatus(account, application) ===
                            'TOPUP_APPROVED' ||
                        getTopUpStatus(account, application) === 'PRE_APPROVED'
                    );
                case 'topup_pending':
                    return (
                        getTopUpStatus(account, application) === 'TOPUP_PENDING'
                    );
                case 'interest_only_extension':
                    return (
                        getTopUpStatus(account, application) ===
                            'INTEREST_ONLY_EXTENSION_APPROVED' ||
                        getTopUpStatus(account, application) ===
                            'INTEREST_ONLY_EXTENSION_APPROVED_WITH_MORE_FUNDS'
                    );
                case 'revolving_credit_facility':
                    return getAccountIsRCF(account);
                default:
                    return false;
            }
        } catch (error) {
            datadogLogs.logger.warn(
                `Feature evaluation failed for "${feature}":`,
                {
                    error: error
                }
            );
            return false;
        }
    }
    /**
  Get all feature flags as a boolean map
     */
    public getAll(): TAccountFeatureMap {
        return this.flags;
    }
    /**
  Check if a specific feature is enabled
     */
    public has(feature: TAccountFeatureKey): boolean {
        return this.flags[feature];
    }
}
